import { parseCalendarDate } from '@lib/datetime'

/**
 * One month of the start-date picker, laid out Sunday-first to match the
 * S M T W T F S header. The grid maths runs over UTC (like every
 * calendar-date helper) so a month's layout never shifts with the viewer's
 * timezone; only the no-date fallback in {@link monthContaining} is
 * deliberately local. Mirrors the iOS flow's `CreateTripCalendarMonth`.
 */

/** A month's identity; `month` is 1-based (1 = January). */
export interface CalendarMonth {
	year: number
	month: number
}

/**
 * The month containing a dashed/compact calendar date. With no (or an invalid)
 * date, falls back to the month of `today` in the *viewer's* timezone — like
 * iOS opening the picker on the local current month — injectable so the
 * fallback stays testable.
 */
export const monthContaining = (
	date: string | null | undefined,
	today: Date = new Date(),
): CalendarMonth => {
	const parts = parseCalendarDate(date)
	if (parts) return { year: parts.year, month: parts.month }
	return { year: today.getFullYear(), month: today.getMonth() + 1 }
}

/** "Aug 2026" */
export const monthTitle = ({ year, month }: CalendarMonth): string =>
	new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
		month: 'short',
		year: 'numeric',
		timeZone: 'UTC',
	})

/** Empty cells before the 1st so weekdays line up under a Sunday-first header. */
export const leadingBlankCount = ({ year, month }: CalendarMonth): number =>
	new Date(Date.UTC(year, month - 1, 1)).getUTCDay()

export const daysInMonth = ({ year, month }: CalendarMonth): number =>
	new Date(Date.UTC(year, month, 0)).getUTCDate()

/** The dashed `YYYY-MM-DD` date of the month's `day` (1-based). */
export const dateForDay = ({ year, month }: CalendarMonth, day: number): string =>
	`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

const shifted = ({ year, month }: CalendarMonth, by: number): CalendarMonth => {
	const zeroBased = month - 1 + by
	return { year: year + Math.floor(zeroBased / 12), month: ((zeroBased % 12) + 12) % 12 + 1 }
}

export const nextMonth = (month: CalendarMonth): CalendarMonth => shifted(month, 1)

export const previousMonth = (month: CalendarMonth): CalendarMonth => shifted(month, -1)
